// AdminPage (BUILD-BRIEF-R2 §13): the Principals panel — the same component the Agents page
// shows — in its compact form, under a rail of five figures the operator could previously get
// only with psql: the accounts, the door's mode, the audit trail's week, the settings and the
// ref counters. Every cell has a producer (GET /api/principals/admin-summary); a cell reads
// `fv--unknown` "not collected" only while that producer has not answered, and `fv--nil` when
// it answered zero. A counted number carries no claim mark — arithmetic makes no claim.
import { getAdminSummary } from '../api/principals.js';
import { Mast, PageShell, Rail, RailCell, Sheet, Zone, ZoneHead } from '../components/PageKit.js';
import PrincipalsPanel from '../components/PrincipalsPanel.js';
import { usePoll } from '../components/usePoll.js';
import { useT } from '../i18n/index.js';
import { tCtx } from '../../shared/i18n.js';
import './AdminPage.de.js';
import './AdminPage.fr.js';

export default function AdminPage() {
  const t = useT();
  const summary = usePoll(getAdminSummary, 60_000);
  const s = summary.data;
  const pending = summary.error ? t('the record did not answer') : t('counting…');
  const nc = t('not collected');

  return (
    <PageShell>
      <Mast
        kicker={t('System')}
        title={t('Admin')}
        sub={t('who may enter, who may call, what the record settled on, and the trail of every change')}
      />
      <Rail>
        <RailCell
          label={t('Operators')}
          value={s?.operators ? s.operators.n : nc}
          tone={!s?.operators ? 'unknown' : s.operators.n === 0 ? 'nil' : 'ink'}
          detail={s?.operators
            ? t('{allowlist} on the allowlist · {admins} admin', { allowlist: s.operators.allowlist, admins: s.operators.admins })
            : s ? t('accounts on the allowlist') : pending}
        />
        <RailCell
          label={t('Registration')}
          value={s?.registration ? (s.registration === 'open' ? tCtx(t, 'open', 'registration') : t('allowlist')) : nc}
          tone={!s?.registration ? 'unknown' : s.registration === 'open' ? 'attend' : 'ok'}
          detail={s?.registration
            ? (s.registration === 'open' ? t('anyone who reaches the login may register') : t('only listed e-mail addresses may log in'))
            : s ? t('allowlist or open') : pending}
        />
        <RailCell
          label={t('Audit entries · 7d')}
          value={s?.audit_7d ? s.audit_7d.n : nc}
          tone={!s?.audit_7d ? 'unknown' : s.audit_7d.n === 0 ? 'nil' : 'ink'}
          detail={s?.audit_7d
            ? (s.audit_7d.n === 0 ? t('no mutation recorded in 7 days') : t('{total} recorded mutations, all time', { total: s.audit_7d.total }))
            : s ? t('recorded mutations') : pending}
        />
        <RailCell
          label={t('Settings')}
          value={s?.settings ? s.settings.n : nc}
          tone={!s?.settings ? 'unknown' : s.settings.n === 0 ? 'nil' : 'ink'}
          detail={s?.settings ? t('rows in app_settings') : s ? t('rows in app_settings') : pending}
        />
        <RailCell
          label={t('Refs minted')}
          value={s?.refs ? s.refs.minted : nc}
          tone={!s?.refs ? 'unknown' : s.refs.minted === 0 ? 'nil' : 'ink'}
          detail={s?.refs
            ? (s.refs.minted === 0 ? t('no ref minted yet') : t('{kinds} counters · monotonic, never reused', { kinds: s.refs.kinds }))
            : s ? t('the counters and their next number') : pending}
        />
      </Rail>

      <PrincipalsPanel compact />

      <Zone>
        <ZoneHead label={t('What will fill the rest')} note={t('settings and the audit trail')} />
        <Sheet pad>
          <p className="zone-empty">{t('The operator allowlist and the audit log already exist behind the auth and audit kits; this page counts them above and is where they will surface in full: the allowlist and its edits, the registration switch, the application settings with their history, the ref counters one by one, and the audit trail with its before/after diffs.')}</p>
        </Sheet>
      </Zone>
    </PageShell>
  );
}
